import { loadAuthConfig } from '@curri/portal-auth'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import '@curri/components/dist/index.css'
import './theme/tokens.css'

import { App } from './App.tsx'
import { AuthProvider } from './auth/AuthProvider.tsx'
import type { PortalConfig } from './types.ts'

// Boot sequence: fetch the environment's config, then render.
//
// The config is served by the API rather than baked in at build time, so one
// built bundle runs against dev, staging, and prod without a rebuild — and the
// OAuth client id, which differs per environment, is never frozen into an asset.
// It is a public document (an OAuth client id is not a secret) and has to be,
// since the portal needs it before it can authenticate anything.

const container = document.getElementById('root')
if (!container) throw new Error('Portal root element is missing')

const root = createRoot(container)

// Tokens, not literals: both stylesheets above are already loaded by the time
// this renders, so the boot failure looks like the rest of the portal.
const fail = (message: string) => {
  root.render(
    <div style={{ maxWidth: 560, margin: '96px auto', padding: '0 24px' }}>
      <h1 style={{ fontSize: 24, margin: 0 }}>Curri Developers</h1>
      <p
        style={{
          marginTop: 12,
          color: 'var(--curri-text-secondary)',
          lineHeight: 1.6,
        }}
      >
        {message}
      </p>
    </div>
  )
}

const boot = async () => {
  const config = await loadAuthConfig<PortalConfig>('/portal.config.json')

  root.render(
    <StrictMode>
      <AuthProvider config={config}>
        <App />
      </AuthProvider>
    </StrictMode>
  )
}

void boot().catch((error: unknown) => {
  fail(
    `The portal could not start: ${
      error instanceof Error ? error.message : 'unknown error'
    }. Reload to try again.`
  )
})
